import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * Cva component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconCva extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property()
  color: IconColorType;

  /**
   * Title
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property()
  size: IconSize;

  /**
   *
   * Append to the class name to pass classes (such as utility classes) into the internals
   */
  @property()
  styleModifier = '';

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon-cva', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M19.9615 4.98102C18.5926 4.77782 17.3734 4.37411 16.2773 3.78056C15.1784 3.18435 14.1999 2.3983 13.3229 1.40906C13.2053 1.27806 13.0609 1.17111 12.9032 1.10427C12.7481 1.03476 12.5796 1 12.4139 1C12.2481 1 12.0797 1.03476 11.9246 1.10427C11.7695 1.17379 11.6225 1.27806 11.5049 1.40906C10.6279 2.3983 9.64936 3.18435 8.5505 3.78056C7.45164 4.37411 6.23515 4.7805 4.86625 4.98102L4 5.10935V8.74815C4.07219 11.4271 4.59622 13.6462 5.35018 15.4616C6.48379 18.186 8.13609 19.9827 9.51033 21.0869C10.8846 22.1938 11.9808 22.6216 12.0503 22.651L12.4139 22.79L12.7775 22.651C12.8737 22.6135 14.7907 21.8649 16.7558 19.7447C17.7371 18.686 18.7236 17.2797 19.4776 15.4643C20.2315 13.6489 20.7556 11.4298 20.8278 8.75082V5.10935L19.9615 4.98102ZM12.4112 11.7907C12.4112 11.903 12.3203 11.9939 12.208 11.9939H6.39556C6.18969 11.002 6.05601 9.91385 6.02393 8.70537V6.82849C8.49435 6.30714 10.6359 5.15748 12.4112 3.39289V11.7907ZM12.4112 20.5923V11.9939H18.4269C17.2371 17.7288 13.5903 19.9987 12.4112 20.5923Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-cva') === undefined) {
  customElements.define('cat-icon-cva', BlocksIconCva);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-cva': BlocksIconCva;
  }
}
